import { Hit } from '@algolia/client-search'
import * as Types from '@graphcommerce/graphql-mesh/.mesh'
import { Image } from '@graphcommerce/image'
import { useCustomerQuery, useCustomerSession } from '@graphcommerce/magento-customer'
import { AddProductsToCartButton } from '@graphcommerce/magento-product'
import { ProductListPrice } from '@graphcommerce/magento-product/components/ProductListPrice/ProductListPrice'
import {
  iconCheckmark,
  iconClose,
  IconSvg,
  responsiveVal,
  extendableComponent,
  iconShoppingBag,
} from '@graphcommerce/next-ui'
import { Trans } from '@lingui/react'
import { ButtonBase, Typography, Box, styled, SxProps, Theme } from '@mui/material'
import { useRouter } from 'next/router'
import { AudienceDocument } from '../../../../graphql/Audience.gql'
import { useStoreData } from '../../../../lib/hooks/useStoreData'
import { ProductSignInButton } from '../../../Product/ProductSignInButton'

export type AlgoliaProductHitItem = Hit<{
  brand: string
  categories: string[]
  image: string
  name: string
  objectID: string
  url: string
  url_key: string
  image_url: string
  image_label_html: string
  merk: string
  sku: string
  delivery: {
    b2c: string
    b2b: string
  }
  type_id: string
  is_salable: {
    b2c: boolean
    b2b: boolean
  }
  price: {
    EUR: {
      default
    }
  }
}>

type PriceFragment = {
  tax: { currency?: Types.CurrencyEnum | null; value?: number | null }
  regular_price: { currency?: Types.CurrencyEnum | null; value?: number | null }
  discount?: { amount_off?: number | null; percent_off?: number | null } | null
  final_price: { currency?: Types.CurrencyEnum | null; value?: number | null }
}

type ProductHitFragment = {
  aspectRatio?: number[]
}

const { classes } = extendableComponent('ProductListItem', [
  'root',
  'item',
  'title',
  'titleContainer',
  'subtitle',
  'price',
  'overlayItems',
  'topLeft',
  'topRight',
  'bottomLeft',
  'bottomRight',
  'imageContainer',
  'placeholder',
  'image',
  'discount',
  'discountPrices',
] as const)

const StyledImage = styled(Image)({})

export function ProductHit(
  props: { hit: AlgoliaProductHitItem; price?: PriceFragment } & ProductHitFragment & {
      sx?: SxProps<Theme>
    },
) {
  const { hit, price, aspectRatio = [4, 3], sx = [] } = props
  const { delivery, is_salable } = hit

  const { loggedIn } = useCustomerSession()
  const { mode } = useStoreData()
  const storeB2B = mode === 'b2b'
  const loggedOutB2B = !loggedIn && storeB2B

  const { locale } = useRouter()
  const urlLocale = locale === 'default' ? '/en' : `/${locale}`

  const { data: audience } = useCustomerQuery(AudienceDocument)
  const customerType = audience?.customer?.audience || 'b2c'
  const inStock = Boolean(is_salable[customerType])

  const getProductUrl = (productItem: AlgoliaProductHitItem) => {
    const urlKey = productItem.url_key
    const url = `/p/${urlKey}`

    return url
  }

  return (
    <ButtonBase
      href={`${urlLocale}${getProductUrl(hit)}`}
      sx={[
        (theme) => ({
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'unset',
          justifyContent: 'unset',
          borderRadius: 0,
          position: 'relative',
          height: '100%',
          backgroundColor: theme.palette.background.paper,
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      className={classes.root}
    >
      <Box
        sx={(theme) => ({
          display: 'grid',
          backgroundColor: 'background.image',
          padding: responsiveVal(10, 20),
          '& > picture': {
            gridArea: `1 / 1 / 3 / 3`,
          },
          '& .ProductWishlistChipBase-wishlistButton': {
            margin: `calc(-1 * ${responsiveVal(10, 20)})`,
            lineHeight: '1',
            '&:hover': {
              background: `none`,
            },
            '& svg:not(.ProductWishlistChipBase-wishlistIconActive)': {
              fill: theme.palette.background.paper,
            },
          },
        })}
        className={classes.imageContainer}
      >
        {hit?.image_label_html && (
          <Box
            component='span'
            sx={{
              position: 'absolute',
              zIndex: 5,
              top: 0,
              left: 0,

              '& .amasty-label-container': {
                display: 'block !important',
              },
              '& img': {
                maxWidth: '85px',
              },
              '& .amlabel-text:empty': {
                display: 'none',
              },
              '& .amlabel-text': {
                borderRadius: 1,
                backgroundColor: 'text.primary',
                color: 'primary.contrastText',
                fontSize: responsiveVal(13, 15),
                padding: '5px 10px',
              },
            }}
            dangerouslySetInnerHTML={{
              // force html to one line and remove whitespace so our :empty selector works
              __html: hit?.image_label_html
                .replace(/[\n\r\t\s]+/g, ' ')
                .replace('<div class="amlabel-text"> </div>', '<div class="amlabel-text"></div>'),
            }}
          />
        )}
        {hit.image_url ? (
          <StyledImage
            layout='fill'
            width={1}
            height={1}
            sizes='300px'
            src={hit.image_url ?? ''}
            alt={hit.name ?? ''}
            className={classes.image}
            sx={{ objectFit: 'contain', aspectRatio: `${aspectRatio[0] / aspectRatio[1]}` }}
          />
        ) : (
          <Box
            sx={{
              gridArea: `1 / 1 / 3 / 3`,
              typography: 'caption',
              display: 'flex',
              textAlign: 'center',
              height: '100%',
              justifyContent: 'center',
              alignItems: 'center',
              color: 'background.default',
              userSelect: 'none',
            }}
            className={`${classes.placeholder} ${classes.image}`}
          >
            <Trans id='No Image' />
          </Box>
        )}
      </Box>
      <svg
        fill='none'
        height='24'
        viewBox='0 0 24 24'
        width='24'
        xmlns='http://www.w3.org/2000/svg'
        style={{ position: 'absolute' }}
      >
        <defs>
          <clipPath id='myCurve'>
            <path d='m5.81956 20.3965s9.60344 8.7128 16.40094-.9562c2.7097-4.6187.931-14.63216-3.5976-17.42672-3.2025-2.751662-16.76684-.36332-17.892481 3.48189-1.533469 7.36313 5.089141 14.90103 5.089141 14.90103z' />
          </clipPath>
        </defs>
      </svg>

      <Box
        className={classes.titleContainer}
        sx={(theme) => ({
          padding: `0 ${responsiveVal(10, 20)}`,
          display: 'grid',
          gridTemplateColumns: 'unset',
          marginTop: theme.spacings.xs,
          rowGap: 1,
          columnGap: 2,
          gridTemplateAreas: `
                  "title"
                  "brand"
                  "sku"
                `,
          [theme.breakpoints.up('md')]: {
            gridTemplateAreas: `
                    "title title"
                    "brand sku"
                  `,
            gridTemplateColumns: 'auto 1fr',
          },
        })}
      >
        <Typography
          variant='h5'
          sx={{
            display: 'inline',
            color: 'text.primary',
            overflowWrap: 'break-word',
            wordBreak: 'break-word',
            maxWidth: '100%',
            gridArea: 'title',
            fontWeight: 'fontWeightBold',
            lineHeight: 1.3,
          }}
          className={classes.title}
        >
          {hit.name}
        </Typography>
        <Box
          sx={() => ({
            gridArea: 'brand',
            color: 'text.secondary',
            typography: 'body1',
          })}
        >
          {hit.merk}
        </Box>
        <Box sx={{ gridArea: 'sku', color: 'text.disabled', typography: 'body1' }}>
          {Array.isArray(hit.sku) ? hit.sku.at(0) : hit.sku}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          padding: responsiveVal(10, 20),
          marginTop: 'auto',
        }}
      >
        {loggedOutB2B ? (
          <ProductSignInButton />
        ) : (
          <>
            {price && (
              <ProductListPrice
                {...price}
                sx={{
                  gridArea: 'price',
                  textAlign: 'right',
                  justifySelf: { sm: 'flex-end' },
                  color: 'primary.main',
                  typography: 'h5',
                }}
              />
            )}
          </>
        )}
        {hit.type_id === 'simple' && (
          <Box sx={{ marginLeft: 'auto' }}>
            {hit.sku && inStock && !loggedOutB2B && (
              <AddProductsToCartButton
                sku={hit.sku}
                size='medium'
                variant='contained'
                color='secondary'
                sx={(theme) => ({
                  px: theme.spacings.xxs,
                })}
              >
                <IconSvg size='large' src={iconShoppingBag} />
              </AddProductsToCartButton>
            )}
          </Box>
        )}
      </Box>
      <Box
        sx={{
          gridArea: 'delivery',
          display: 'grid',
          gridTemplateColumns: `min-content auto`,
          alignItems: 'start',
          gap: responsiveVal(5, 10),
          padding: responsiveVal(10, 20),
          color: inStock ? 'secondary.main' : 'text.main',

          '& > span': {
            lineHeight: 1.2,
          },
        }}
      >
        <IconSvg
          src={inStock ? iconCheckmark : iconClose}
          sx={[
            { flexShrink: 0 },
            inStock && {
              transform: 'translateY(-4px)',
              alignSelf: 'start',
            },
            !inStock && {
              transform: 'translateY(-3px)',
              alignSelf: 'start',
            },
          ]}
        />
        <Typography
          variant='caption'
          dangerouslySetInnerHTML={{ __html: delivery[customerType] }}
        />
      </Box>
    </ButtonBase>
  )
}
